import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { updateReservation, reservationUpdateNotify, validateSessionCheckIn } from '../../services/UpdateReservation'

import { DomainContext, getDriversFirstName, mapDriversPhone } from '../../domain-repository/DomainRepository'
import { GroupsImagesContext } from '../../domain-repository/GroupsImagesRepository'

import DriverInfoHeader from '../../components/PageHeader'
import ReviewBanner from '../../components/review/ReviewBanner'
import ReviewStations from '../../components/review/ReviewStations'
import ReviewVehicleProperties from '../../components/review/ReviewVehicleProperties'
import { AlertModal } from '../../components/alert-modal/AlertModal'
import withLang from '../../withLang'
import Loading from '../../components/Loading'
import { saveSessionEnd } from '../../services/Metrics'
import alertModalManager from '../../components/alert-modal-wraper/alert-modal-manager'
import TrackingPage from '../../components/TrackingPage'
import { getLocalTimeZone } from '../../utils/utils'
import { hasDriverValues } from '../driver/driverInfoStepUtils'

function ReviewFinalReservationPage(props) {
  const { t, i18n } = useTranslation();

  const {
    reservation,
    selectedExtras,
    reservationExtras,
    rentalAgreementQuoteValue,
    getVehicleForRA,
    driverInfo,
    additionalDriverInfo,
    upselling,
    getExcessForExtras,
    hasAdditionalDriver,
    getDriversPhoneCountryCode,
    isIncompatibleExtra,
    appFlowConfigs,
    removedExtrasInReview,
    groupsDetails,
    setNotifyUserResult,
    language,
    getBestPackForExtras,
    setSessionId,
    canGoInsurancePacksPage,
    sessionExtras,
    stationsConfigurations,
    localizationAssets,
  } = useContext(DomainContext)

  const safeReservation = reservation || {}
  const safeDriverInfo = driverInfo || {}

  const [isValidatingSession, setIsValidatingSession] = useState(false)

  useEffect(() => {
    setIsValidatingSession(true)
    
    if (!hasDriverValues(driverInfo)) {
      alertModalManager.openModal({ title: t('errorTitle'), message: t('errorMessage') })
      setIsValidatingSession(false)
    } else {

    }

    validateSessionCheckIn()
      .then((checkedIn) => {
        if (checkedIn) {
          alertModalManager.openModal({ title: t('errorTitle'), message: t('errorMessage') })
        }
      })
      .finally((res) => {
        setIsValidatingSession(false)
      })
  }, [])

  const vehicle = getVehicleForRA()

  const { groupsConfigurations } = useContext(GroupsImagesContext)

  const groupInfo = groupsDetails.find(g => g.id === vehicle) || {}

  const [loading, setIsLoading] = useState(false)

  const [reservationPopupInfo, setReservationPopupInfo] = useState({
    isVisible: false,
    message: '',
    title: ''
  })

  function handleOpenExtrasClick() {
    props.history.push('/review-final-reservation-extras')
  }

  function handleNextClick() {
    submitUpdateReservation()
  }

  function submitUpdateReservation() {
    function prepareExtras() {
      const ALWAYS_PRESENT = ['TP', 'CDW']
      const configuredPacks = (reservation && safeReservation.packs ? safeReservation.packs : [])

      const currentExtrasToCheck = [...selectedExtras.map(e => e.extraID), ...reservationExtras.map(e => e.extraID)]

      if (!canGoInsurancePacksPage()) {
        return currentExtrasToCheck.join(',')
      }

      /*
      const packWithMatchingCurrentExtras = getBestPackForExtras(currentExtrasToCheck, configuredPacks, true)

      if (packWithMatchingCurrentExtras) {
        const packSelectedInKiosk = packWithMatchingCurrentExtras.extrasList.some(e => selectedExtras.find(se => se.extraID === e)) || selectedExtras.find(se => se.extraID === packWithMatchingCurrentExtras.extraId)
        if (packSelectedInKiosk) {
          const extraAvail = safeReservation.availableExtras.find(e => e.extraID === packWithMatchingCurrentExtras.extraId)
          const validExtras = currentExtrasToCheck.filter(e =>
            !packWithMatchingCurrentExtras.extrasList.filter(e => !ALWAYS_PRESENT.includes(e)).includes(e) &&
            (extraAvail && extraAvail.must_not_include && extraAvail.must_not_include.split(',').find(mni => mni === e) === undefined) &&
            packWithMatchingCurrentExtras.extraId !== e
          )
          return [packWithMatchingCurrentExtras.extraId, ...validExtras].join(',')
        }
      }
      */

      const matchingPackFromReservation = configuredPacks.find(p => p.extraId !== 'IF' && p.extraId !== 'RF' && currentExtrasToCheck.find(e => e === p.extraId) !== undefined)
      const revExtraValid = matchingPackFromReservation ?
        reservationExtras
          .filter(r => !matchingPackFromReservation.extrasList.filter(e => !ALWAYS_PRESENT.includes(e)).includes(r.extraID)) : reservationExtras

      return [...selectedExtras.map(e => e.extraID), ...revExtraValid.map(e => e.extraID)].join(',')
    }

    if (loading) { return; }
    const extrasForMetrics = selectedExtras.map(e => ({ extraId: e.extraID, price: e.totalPrice }))

    if (upselling?.groupID) {
      extrasForMetrics.push({ extraId: 'UPSELLING', price: upselling.previewValue, metainfo: JSON.stringify({ group: upselling.groupID }) })
    }

    const sessionExtrasMapped = sessionExtras.map(se => ({
      ...se,
      removedInReview: removedExtrasInReview.some(e => e.extraId === se.extraId),
      acquired: removedExtrasInReview.some(e => e.extraId === se.extraId) ? false : se.acquired,
    }))

    const reservationInfo = {
      removedExtrasInReview,
      acquiredExtras: extrasForMetrics,
      sessionExtras: sessionExtrasMapped,
      driversCountryID: driverInfo.countryID,
      driversLicenseNumber: driverInfo.driversLicense,
      driversLicenseIssuedPlace: driverInfo.driversLicenseIssuedPlace || driverInfo.iDIssuedPlace || 'Default',
      driversLicenseIssuedDate: driverInfo.driversLicenseIssuedDate,
      driversLicenseExpirationDate: driverInfo.driversLicenseExpirationDate,
      driversName: driverInfo.fullName,
      driversBirthDate: driverInfo.birthDate,
      driversAddress: driverInfo.address,
      driversDoorNumber: driverInfo.doorNumber,
      driversPostalCode: driverInfo.postalCode,
      driversEmail: driverInfo.email || '',
      driversPhone: mapDriversPhone(driverInfo.phone, getDriversPhoneCountryCode()),
      driversIdNumber: driverInfo.documentID,
      driversIdIssuedPlace: driverInfo.iDIssuedPlace || '',
      driversIdExpireDate: driverInfo.iDExpireDate || '',
      driversTaxNumber: driverInfo.taxNumber || '',
      driversDocumentType: driverInfo.documentType || '',
      driversCity: driverInfo.city || '',
      clientID: safeReservation.clientID,
      reservationNumber: safeReservation.Reservation,
      reservationPickupDate: safeReservation.PickupDate,
      pickupStationTimeZone: getLocalTimeZone(),
      reservationPickupStationId: safeReservation.PickupStationID,
      reservationDropOffDate: safeReservation.DropOffDate,
      reservationDropOffStationId: safeReservation.DropOffStationID,
      dropOffStationName: safeReservation.DropOffStation,
      pickupStationName: safeReservation.PickupStation,
      vehicleType: groupInfo.type || '',
      reservationReference: safeReservation.Reference || '',
      rateCode: safeReservation.RateCode,
      group: safeReservation.GroupID,
      groupUpselling: upselling.groupID,
      extras: prepareExtras(),
      nationality: language,
      isPastPickupHour: safeReservation.isPastPickupHour,
      salesOnly: safeReservation.salesOnly,
      disableSales: safeReservation.disableSales,
    };

    if (hasAdditionalDriver()) {
      reservationInfo.additionalDriverName = additionalDriverInfo.fullName;
      reservationInfo.additionalDriverBirthDate = additionalDriverInfo.birthDate;
      reservationInfo.additionalDriverLicenseNumber = additionalDriverInfo.driversLicense;
      reservationInfo.additionalDriverLicenseIssuedPlace = additionalDriverInfo.driversLicenseIssuedPlace;
      reservationInfo.additionalDriverLicenseIssuedDate = additionalDriverInfo.driversLicenseIssuedDate;
      reservationInfo.additionalDriverLicenseExpirationDate = additionalDriverInfo.driversLicenseExpirationDate;
      reservationInfo.additionalDriverIdNumber = additionalDriverInfo.documentID;
      reservationInfo.additionalDriverIdCountry = additionalDriverInfo.iDIssuedCountry;
      reservationInfo.additionalDriverIdExpireDate = additionalDriverInfo.iDExpireDate;
    }
    setIsLoading(true)
    updateReservation(reservationInfo, i18n.language)
      .then(results => {
        setIsLoading(false)
        if (results.skipNotify) {
          saveSessionEnd(false)
          props.history.push('/thank-you')
        } else {
          handleReservationUpdateNotify()
        }
      })
      .catch(err => {
        setIsLoading(false)
        alertModalManager.openModal({ title: t('errorTitle'), message: t('errorMessage') })
      })
  }

  const handleReservationUpdateNotify = () => {
    setIsLoading(true);
    reservationUpdateNotify()
      .then((res) => {
        saveSessionEnd(false)
        setNotifyUserResult(res)
        props.history.push('/thank-you')
      })
      .catch(e => {
        setIsLoading(false);
        alertModalManager.openModal({ title: t('errorTitle'), message: t('errorMessage') })
      })
  }

  function getStationName(stationId) {
    const station = stationsConfigurations.find(s => s.stationId === stationId)
    return station ? t(station.name) : `Station ${stationId}`
  }

  return (
    <div>
      <DriverInfoHeader
        title={t('rentalAgreementOverview')}
        active={3}
        progress={'100%'}
        handleLogoClick={() => props.history.push('/start')}
      />
      <main>
        <div className="container grow">
          <Loading active={loading} message={t('updatingReservation')} />

          <div className="main-frame-resumo">
            <ReviewBanner
              displayLogo={false}
              logoImgSrc={require(`../../assets/images/logo.png`)}
              groupId={vehicle}
              groupType={groupInfo?.type}
              groupImgSrc={groupsConfigurations[safeReservation.GroupID] && groupsConfigurations[safeReservation.GroupID].imageUrl}

              driverName={getDriversFirstName(driverInfo.fullName)}
              reference={safeReservation.Reference}
            />
            <ReviewVehicleProperties
              seats={groupInfo?.seats}
              consumption={groupsConfigurations[vehicle] && groupsConfigurations[vehicle].consumption + ` (${t('vehicles-' + groupInfo?.fuel, '')})`}
              doors={groupInfo?.doors}
              bags={groupInfo?.bags}
              handbags={groupInfo?.handbags}
              fuel={groupInfo?.fuel}
              transmission={groupInfo?.transmission}
              displayExcess={appFlowConfigs?.showExcessValue || reservation.isExclusiveReservation}
              excessForExtras={getExcessForExtras([...reservationExtras, ...selectedExtras])}
              localizationAssets={localizationAssets}
            />
            <ReviewStations
              pickupStation={getStationName(safeReservation.PickupStationID)}
              pickupDate={moment(safeReservation.PickupDate, 'YYYY-MM-DD HH:mm').locale(i18n.language).format('ddd D MMM / HH:mm')}
              dropOffStation={getStationName(safeReservation.DropOffStationID)}
              dropOffDate={moment(safeReservation.DropOffDate, 'YYYY-MM-DD HH:mm').locale(i18n.language).format('ddd D MMM / HH:mm')}
            />
            <div className="col-12 col-lg-4 board-background text-center p-3  mb-4">
              <div onClick={handleOpenExtrasClick} className="btn btn-green text-uppercase"><b>{t('included')}</b></div>
            </div>
          </div>
        </div>
        <ReviewFooter
          style={{ position: 'fixed', bottom: '0'}}
          hidePrice={safeReservation.isPastPickupHour}
          totalPrice={rentalAgreementQuoteValue}
          handlePrevClick={() => props.history.goBack()}
          handleNextClick={handleNextClick}
          disabled={isValidatingSession}
          
        />
        <AlertModal
          isOpen={reservationPopupInfo.isVisible}
          message={reservationPopupInfo.message}
          title={reservationPopupInfo.title}
          buttonLabel='OK'
          handleClick={() => {
            setReservationPopupInfo({ isVisible: false });
            props.history.push('/');
          }}
        />
      </main>
    </div>
  )
}

export default withLang(TrackingPage(ReviewFinalReservationPage, 85))

function ReviewFooter({ style, handleNextClick, handlePrevClick, totalPrice, hidePrice, disabled }) {
  const { t } = useTranslation();

  return (
    <div className="footer" style={style}>
      <div className="navbar">
        {disabled ? null :
          <>
            <div onClick={handlePrevClick} className='prev'><img src={require(`../../assets/images/btn-prev-2.png`)} alt="Back" /></div>
            {hidePrice ? null : <div className="text-center"><b>{t('total')}</b> € {totalPrice}</div>}
            <div onClick={handleNextClick} className="footer-final btn btn-orange"><b>{t('finalize')}<br />{t('reservation')}</b></div>
          </>
        }
      </div>
    </div>
  )

}
